import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getDistrictJobs,
  pauseDistrictIntake,
  resumeDistrictIntake,
  pauseDistrictJob,
  startDistrictJob,
  stopDistrictJob,
  restartDistrictJob,
} from '../../../services/districtJobService';

const EMPTY = {
  intake_paused: false,
  can_control: false,
  running: [],
  queued: [],
  paused: [],
  recent: [],
  slots: {},
};

export function jobConfirmConfig(action, job) {
  const name = job?.student_name || 'this student';
  const queued = String(job?.status || '').toLowerCase() === 'queued';
  const configs = {
    pause: {
      title: 'Pause job',
      message: queued
        ? `Pause the queued job for ${name}? It moves to Paused — use Start within 30 days when you want it to run.`
        : `Pause the job for ${name}? Progress is saved — use Start within 30 days to continue.`,
      confirmText: 'Pause job',
      variant: 'warning',
    },
    start: { title: 'Start job', message: `Start this paused job for ${name}? It continues from the last checkpoint (within 30 days).`, confirmText: 'Start job', variant: 'info' },
    stop: {
      title: 'Stop and remove job',
      message: queued
        ? `Stop the queued job for ${name} and remove it from the list? It will not start.`
        : `Stop this job for ${name} and remove it from the list? This cannot be undone for the current run.`,
      confirmText: 'Stop and remove',
      variant: 'danger',
    },
    restart: { title: 'Restart compliance', message: `Restart compliance for ${name}? A new re-run will be queued.`, confirmText: 'Restart', variant: 'warning' },
  };
  return configs[action] || { title: 'Confirm', message: 'Are you sure?', confirmText: 'Confirm', variant: 'info' };
}

export function intakeConfirmConfig(pause) {
  return pause
    ? { title: 'Pause intake', message: 'Pause pipeline intake for the whole district? Queued jobs wait until you resume. Running jobs finish their current step.', confirmText: 'Pause intake', variant: 'warning' }
    : { title: 'Resume intake', message: 'Resume pipeline intake? Queued jobs start when a worker is free.', confirmText: 'Resume intake', variant: 'info' };
}

function errorText(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || d).join(' ');
  return detail?.msg || detail?.message || fallback;
}

/** District jobs: polled every 6 s, with director controls and confirmations. */
export default function useDistrictJobs({ isDirector }) {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);
  const [alert, setAlert] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [intakeBusy, setIntakeBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [now, setNow] = useState(Date.now());
  const lastOkRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: res } = await getDistrictJobs();
      const p = res?.data || {};
      setData({
        intake_paused: Boolean(p.intake_paused),
        can_control: p.can_control !== undefined ? Boolean(p.can_control) : isDirector,
        running: p.running || [],
        queued: p.queued || [],
        paused: p.paused || [],
        recent: p.recent || [],
        slots: p.slots || {},
      });
      lastOkRef.current = new Date();
      setStale(false);
      setAlert((prev) => (prev?.source === 'load' ? null : prev));
    } catch (err) {
      setStale(true);
      const last = lastOkRef.current;
      setAlert({
        source: 'load',
        type: 'warning',
        message: `${errorText(err, 'Could not load pipeline jobs')}.${last ? ` Showing the last update from ${last.toLocaleTimeString()}.` : ''}`,
      });
    } finally {
      setLoading(false);
    }
  }, [isDirector]);

  useEffect(() => {
    load();
    const poll = setInterval(load, 6000);
    const tick = setInterval(() => setNow(Date.now()), 15000);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, [load]);

  const canControl = Boolean(data.can_control) && isDirector;

  const requestJobAction = (action, job) => {
    if (canControl) setConfirm({ kind: 'job', action, job, ...jobConfirmConfig(action, job) });
  };
  const requestIntake = (pause) => {
    if (canControl) setConfirm({ kind: 'intake', pause, ...intakeConfirmConfig(pause) });
  };

  const runConfirmed = async () => {
    if (!confirm) return;
    if (confirm.kind === 'intake') {
      setIntakeBusy(true);
      try {
        const { data: res } = confirm.pause ? await pauseDistrictIntake() : await resumeDistrictIntake();
        setAlert({ type: 'info', message: res?.data?.alert || res?.message || (confirm.pause ? 'Intake paused.' : 'Intake resumed.') });
        await load();
      } catch (err) {
        setAlert({ type: 'error', message: errorText(err, 'Intake control failed.') });
      } finally {
        setIntakeBusy(false);
        setConfirm(null);
      }
      return;
    }
    const { action, job } = confirm;
    setBusyId(job.job_id);
    try {
      const call = { pause: pauseDistrictJob, start: startDistrictJob, stop: stopDistrictJob, restart: restartDistrictJob }[action];
      const res = await call(job.job_id);
      const body = res?.data || {};
      const msg = body?.data?.alert || body?.message || 'Request accepted. Changes may take a short time to apply.';
      if (action === 'stop') {
        const id = job.job_id;
        setData((prev) => ({
          ...prev,
          paused: (prev.paused || []).filter((j) => j.job_id !== id),
          queued: (prev.queued || []).filter((j) => j.job_id !== id),
          running: (prev.running || []).filter((j) => j.job_id !== id),
        }));
      }
      setAlert({ type: action === 'stop' || action === 'restart' ? 'warning' : 'info', message: String(msg) });
      await load();
    } catch (err) {
      setAlert({ type: 'error', message: errorText(err, `Failed to ${action} job.`) });
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  return {
    data,
    loading,
    stale,
    alert,
    dismissAlert: () => setAlert(null),
    busyId,
    intakeBusy,
    confirm,
    cancelConfirm: () => { if (!busyId && !intakeBusy) setConfirm(null); },
    runConfirmed,
    canControl,
    requestJobAction,
    requestIntake,
    refresh: () => { setLoading(true); load(); },
    now,
  };
}
